import type { AwsWafConfig, AwsWafRemoteConfig } from '../types';
import { addBreadcrumb } from '../utilities/analytics/error-reporting';
import { isFedM } from '../utilities/env';
import { selectAwsWafApiKey } from './aws-captcha.helpers';

/**
 * Fetches the AWS WAF config from the edge endpoint served same-origin by the WAF on every
 * CloudFront distribution, and resolves it for the current hostname.
 *
 * The endpoint returns every API key the WebACL accepts (AWS caps a key at 5 token domains, so a
 * WebACL accepting more ships several keys in one static response). The hostname is fixed for the
 * page's lifetime, so we select the matching key once here and hand consumers a flat
 * {@link AwsWafConfig} — no per-use re-selection. A non-matching (or absent) key resolves to
 * `undefined` so no CAPTCHA is rendered (never the wrong key — see `selectAwsWafApiKey`).
 *
 * Intentionally different from `/frontend/state`:
 * - Fetched via a relative URL (no `getIdacOrigin()` prefix) so it resolves against the current
 *   host — the WAF response has no `Access-Control-Allow-Origin`, so it must be same-origin.
 * - `credentials: 'omit'` — the endpoint is unauthenticated and requires no cookies.
 * - Best-effort: any failure resolves to `undefined` so the app boots without a CAPTCHA config
 *   (downstream treats an absent config as "no CAPTCHA") rather than blocking startup.
 *
 * Skipped entirely on FedRAMP Moderate, where AWS WAF CAPTCHA is not used.
 */
export async function fetchAwsWafConfig(): Promise<AwsWafConfig | undefined> {
  if (isFedM()) {
    return undefined;
  }

  try {
    const res = await fetch('/_edge/waf/config', { credentials: 'omit' });

    if (!res.ok) {
      addBreadcrumb({
        message: `AwsWafConfig: fetch failed with status ${res.status}`,
        level: 'warning',
      });
      return undefined;
    }

    const body = await res.json();
    const remoteConfig: AwsWafRemoteConfig | undefined = body?.awsWafConfig;
    const apiKey = selectAwsWafApiKey(remoteConfig);

    if (!remoteConfig?.integrationUrl || !apiKey) {
      addBreadcrumb({
        message: 'AwsWafConfig: fetch succeeded but no config resolved for this hostname',
        level: 'warning',
      });
      return undefined;
    }

    addBreadcrumb({ message: 'AwsWafConfig: fetch succeeded' });
    return {
      integrationUrl: remoteConfig.integrationUrl,
      baseUrl: remoteConfig.baseUrl,
      apiKey,
    };
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : 'Unknown error';
    addBreadcrumb({
      message: `AwsWafConfig: fetch error - ${errorMessage}`,
      level: 'warning',
    });
    return undefined;
  }
}
